// The W5 (V5R1) wallet's address, from its initial state.
//
// A TON address is the hash of the wallet CONTRACT's initial state — the
// compiled code plus a data cell holding the public key — so deriving one
// means hashing cells. See ./cell.ts for why that is done here rather than
// with @ton/core, and for the measurement behind it.
//
// Three constants carry the contract: the code cell's hash and depth, which is
// all the state-init hash needs of it, and the wallet id baked into the data.
// Every one was read off @ton/ton's own WalletContractV5R1, and the test
// beside this file rebuilds these addresses with that class across many keys
// and asserts they match. If the official implementation ever changes, that
// test fails rather than deposits arriving at an address we no longer derive.

import { cellDepth, cellHash, type CellRepr } from './cell';

/**
 * Hash of the compiled W5 wallet code.
 *
 * The code's CONTENTS are not needed to compute an address: a cell's
 * representation references its children by hash and depth only. The bytes are
 * needed to DEPLOY the wallet, which happens on the first send — and that path
 * loads @ton/ton, which ships the contract itself.
 */
const CODE_HASH = '20834b7b72b112147e1b2fb457b84e74d1a30f04f737d4f62a668e9552d2b72f';

/** Depth of that code cell — it has nested refs, so this is not zero. */
const CODE_DEPTH = 6;

/**
 * The wallet id baked into a W5 wallet's data.
 *
 * Not a free choice: it encodes the network, the workchain, the wallet version
 * and the subwallet number, and the contract checks it on every signed
 * message. This is the value for mainnet, basechain, v5r1, subwallet 0 — the
 * only combination we create.
 */
const WALLET_ID_MAINNET = 0x7fffff11;

/** The data cell is 322 bits with no refs: 1 + 32 + 32 + 256 + 1. */
const DATA_BITS = 322;

function hexToBytes(hex: string): Uint8Array {
  const out = new Uint8Array(hex.length / 2);
  for (let i = 0; i < out.length; i++) out[i] = Number.parseInt(hex.slice(i * 2, i * 2 + 2), 16);
  return out;
}

const codeRef = { hash: hexToBytes(CODE_HASH), depth: CODE_DEPTH };

/**
 * The wallet's data cell, as something hashable.
 *
 * Field order and widths are the contract's. Getting any of them wrong yields
 * a perfectly valid address for a contract that is not a wallet.
 */
function dataCell(publicKey: Uint8Array): CellRepr {
  if (publicKey.length !== 32) throw new Error('ton: expected a 32-byte public key');
  // 322 bits = 40 whole bytes plus 2, so the last byte is partial and the
  // representation marks where the bits stop.
  const bytes = new Uint8Array(41);
  // bit 0: is_signature_allowed = 1. Bits 1..32 (seqno) and 33..64 minus the
  // wallet id are then written over the zeroed buffer.
  bytes[0] = 0x80;
  // seqno = 0 across bits 1..32, already zero.
  // wallet id occupies bits 33..64, which straddles bytes 4..8.
  writeBits(bytes, 33, WALLET_ID_MAINNET, 32);
  // public key occupies bits 65..320, starting mid-byte like the id.
  writeBytes(bytes, 65, publicKey);
  // bit 321: extensions, an empty dict = 0. Already zero.
  return { bitLength: DATA_BITS, bytes, refs: [] };
}

/** Write `width` bits of `value` starting at bit `offset`. */
function writeBits(out: Uint8Array, offset: number, value: number, width: number): void {
  for (let i = 0; i < width; i += 1) {
    if ((value >>> (width - 1 - i)) & 1) {
      const at = offset + i;
      out[at >> 3]! |= 0x80 >> (at & 7);
    }
  }
}

/** Write whole bytes starting at an arbitrary bit offset. */
function writeBytes(out: Uint8Array, offset: number, data: Uint8Array): void {
  for (let byte = 0; byte < data.length; byte += 1) {
    writeBits(out, offset + byte * 8, data[byte]!, 8);
  }
}

/**
 * The StateInit cell: five flag bits and two references.
 *
 * Bits are split_depth (none), special (none), code present, data present,
 * library (none) — 0b00110, which with the representation's end marker becomes
 * the single byte 0x34.
 */
function stateInit(publicKey: Uint8Array): CellRepr {
  const data = dataCell(publicKey);
  const refs = [codeRef, { hash: cellHash(data), depth: cellDepth(data.refs) }];
  return {
    bitLength: 5,
    bytes: Uint8Array.of(0b00110000),
    refs,
  };
}

/** The wallet's 32-byte account id — the hash of its initial state. */
export function tonWalletHash(publicKey: Uint8Array): Uint8Array {
  return cellHash(stateInit(publicKey));
}

/** The wallet's raw address (`0:<hex>`), in the basechain. */
export function tonWalletRawAddress(publicKey: Uint8Array): string {
  return `0:${Buffer.from(tonWalletHash(publicKey)).toString('hex')}`;
}
